import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Form, Input, Select, DatePicker, InputNumber, Button, Space, Divider } from 'antd'
import moment from 'moment'
import { FormWrapper, FormSection, ReadOnlyField, FormActions, CampaignHeader } from './CampaignForm.styles'
import useAction from 'utils/useAction'
import * as arcCampaignsActions from 'actions/views/arcCampaigns'
import * as arcAdvertisersActions from 'actions/views/arcAdvertisers'
import { selectors as arcAdvertisersSelectors } from 'domains/arcAdvertisers'

const { Option } = Select

const CampaignForm = ({ campaign, onSubmit, onCancel }) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  const createCampaign = useAction(arcCampaignsActions.createArcCampaign)
  const updateCampaign = useAction(arcCampaignsActions.updateArcCampaign)
  const fetchAdvertisers = useAction(arcAdvertisersActions.fetchArcAdvertisers)
  const advertisers = useSelector(arcAdvertisersSelectors.getArcAdvertisers)

  const isEditMode = !!campaign

  useEffect(() => {
    // Fetch advertisers when component mounts
    fetchAdvertisers({ page: 1, search: '', filters: {}, sorter: {} })
  }, [fetchAdvertisers])

  useEffect(() => {
    if (campaign) {
      form.setFieldsValue({
        source: campaign.source,
        brand_name: campaign.brand_name,
        offer: campaign.offer,
        started_at: campaign.started_at ? moment(campaign.started_at) : null,
        end_at: campaign.end_at ? moment(campaign.end_at) : null,
        impression_cap: campaign.impression_cap,
        click_cap: campaign.click_cap,
        arc_advertiser_id: campaign.arc_advertiser_id,
      })
    }
  }, [campaign, form])

  const handleValuesChange = (changedValues, allValues) => {
    // Values change handler can be used for other logic if needed
  }

  const handleSubmit = async (values) => {
    setLoading(true)
    try {
      let data

      if (isEditMode) {
        data = {
          end_at: values.end_at ? values.end_at.toISOString() : null,
          impression_cap: values.impression_cap || null,
          click_cap: values.click_cap || null,
        }

        await updateCampaign({
          id: campaign.id,
          data,
          callback: onSubmit
        })
      } else {
        data = {
          brand_name: values.brand_name,
          offer: values.offer,
          source: values.source,
          started_at: values.started_at ? values.started_at.toISOString() : null,
          end_at: values.end_at ? values.end_at.toISOString() : null,
          impression_cap: values.impression_cap || null,
          click_cap: values.click_cap || null,
          arc_advertiser_id: values.arc_advertiser_id,
        }

        await createCampaign({
          data,
          callback: onSubmit
        })
      }
    } catch (error) {
      console.error('Failed to save campaign:', error)
    } finally {
      setLoading(false)
    }
  }


  return (
    <FormWrapper>
      {isEditMode && campaign && campaign.name && (
        <CampaignHeader>
          <div className="campaign-label">Campaign Name</div>
          <div className="campaign-name">{campaign.name}</div>
        </CampaignHeader>
      )}
      {!isEditMode && (
        <CampaignHeader>
          <div className="campaign-label">New Campaign</div>
          <div className="campaign-name">Create New Arc Campaign</div>
        </CampaignHeader>
      )}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleValuesChange}
        autoComplete="off"
      >
        <FormSection>
          <Form.Item
            name="source"
            label="Source"
            rules={[{ required: true, message: 'Please select a source' }]}
          >
            <Select placeholder="Select source" disabled={isEditMode}>
              <Option value="karma">Karma</Option>
              <Option value="imageye">Imageye</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="brand_name"
            label="Brand Name"
            rules={[{ required: true, message: 'Please enter brand name' }]}
          >
            <Input placeholder="Enter brand name" disabled={isEditMode} />
          </Form.Item>

          <Form.Item
            name="offer"
            label="Offer"
            rules={[{ required: true, message: 'Please enter offer' }]}
          >
            <Input placeholder="Enter offer (e.g., 50% off, Free shipping)" disabled={isEditMode} />
          </Form.Item>

          <Form.Item
            name="arc_advertiser_id"
            label="Arc Advertiser"
            rules={[{ required: true, message: 'Please select an Arc Advertiser' }]}
          >
            <Select
              placeholder="Select advertiser"
              allowClear
              disabled={isEditMode}
            >
              {advertisers.map(advertiser => (
                <Option key={advertiser.id} value={advertiser.id}>
                  {advertiser.attributes?.name || advertiser.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </FormSection>

        <Divider />

        <FormSection>
          <Form.Item
            name="started_at"
            label="Start Date"
            rules={[{ required: true, message: 'Please select start date' }]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              style={{ width: '100%' }}
              disabled={isEditMode}
            />
          </Form.Item>

          <Form.Item
            name="end_at"
            label="End Date (Optional)"
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </FormSection>

        <FormSection>
          <Form.Item
            name="impression_cap"
            label="Impression Cap (Optional)"
          >
            <InputNumber
              min={0}
              placeholder="Leave empty for unlimited"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="click_cap"
            label="Click Cap (Optional)"
          >
            <InputNumber
              min={0}
              placeholder="Leave empty for unlimited"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </FormSection>

        <Divider />

        {!isEditMode && (
          <FormSection>
            <ReadOnlyField>
              <div className="label">Campaign Name:</div>
              <div className="value">Will be auto-generated based on source, brand, and offer</div>
            </ReadOnlyField>
          </FormSection>
        )}

        {isEditMode && campaign && (
          <FormSection>
            <ReadOnlyField>
              <div className="label">Impressions:</div>
              <div className="value">
                {!campaign.impression_count && !campaign.impression_cap ? '-' : `${campaign.impression_count || 0} / ${campaign.impression_cap || '-'}`}
              </div>
            </ReadOnlyField>

            <ReadOnlyField>
              <div className="label">Clicks:</div>
              <div className="value">
                {!campaign.click_count && !campaign.click_cap ? '-' : `${campaign.click_count || 0} / ${campaign.click_cap || '-'}`}
              </div>
            </ReadOnlyField>
          </FormSection>
        )}

        <FormActions>
          <Space>
            <Button onClick={onCancel}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {isEditMode ? 'Update Campaign' : 'Create Campaign'}
            </Button>
          </Space>
        </FormActions>
      </Form>
    </FormWrapper>
  )
}

export default CampaignForm